import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStateValue } from '../../../../../../context/AppContext';
import { createCleanMarkup, scrollTo } from '../../../../../../utils/common';
import HelpNav from '../../../../common/HelpNav';
import {
  CmsContent,
  ConfigKey,
  FlowData,
  Room,
  getConfigKey,
  EVENT_FLOW_CONFIGS,
  STEP_COMPONENT,
} from '../../../../common/proctorStepsTypes';
import ProgressBar from '../../../../common/ProgressBar/ProgressBar';
import RoomCode from '../../../../common/RoomCode';
import StepContentPanel from '../../../../common/StepContentPanel';
import StepContentTab from '../../../../common/StepContentTab';
import WizardNav from '../../../../common/WizardNav';
import './Attendance.scss';
import StudentRoster from './StudentRoster';
import { useSkipLink } from '../../../../../../hooks/useSkipLink';

interface GetStepDefinitionsProps {
  cmsContent: CmsContent;
  stepNum: number;
  configKey: ConfigKey;
  room: Room;
  lastUpdated: string;
  refetch: () => void;
  rosterLinkText: string;
}

function GetStepDefinitions({
  cmsContent,
  stepNum,
  configKey,
  room,
  lastUpdated,
  refetch,
  rosterLinkText,
}: GetStepDefinitionsProps) {
  const { introStep, outerStep, progressStepperMax, steps: stepConfig } = EVENT_FLOW_CONFIGS[configKey].attendance;

  const stepContent = cmsContent?.steps?.find((n) => n.step_number === stepNum);
  const mainPanelData = <div dangerouslySetInnerHTML={createCleanMarkup(stepContent?.main_panel_content)} />;
  const detailPanelData = <div dangerouslySetInnerHTML={createCleanMarkup(stepContent?.detail_panel_content)} />;

  const content = {
    main_panel_content: mainPanelData,
    main_panel_header: stepContent?.main_panel_header,
    detail_panel_content: detailPanelData,
    detail_panel_header: stepContent?.detail_panel_header,
  };

  const stepComponents: Partial<Record<STEP_COMPONENT, React.ReactElement>> = {
    defaultSideBySideContent: (
      <>
        <StepContentPanel
          content={content}
          desc={mainPanelData}
          img={<div className='script-page-svg attendance-step-illustration-img' />}
          nextUpStyle={true}
          progressStepperMax={progressStepperMax}
          room={room}
          showProgressBar={true}
          showPanelHeader={true}
          stepNum={stepNum}
        ></StepContentPanel>
      </>
    ),
    defaultContentTabLight: (
      <>
        <StepContentTab
          showProgressBar={true}
          content={content}
          darkMode={false}
          invertStyle={false}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    defaultContentTabDark: (
      <>
        <StepContentTab
          showProgressBar={true}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    checkInScript: (
      <>
        <StepContentTab
          heading='Check In Script'
          showProgressBar={false}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    takeAttendance: (
      <div>
        <div className='row row-eq-height px-0 mx-0 wizard-stepper-heading'>
          <div className='col-xs-12 col-md-7 px-0'>
            <ProgressBar currentStep={stepNum} totalSteps={progressStepperMax}></ProgressBar>
          </div>
          <div className='wizard-right-panel col-xs-12 col-md-5 px-0'>
            <div className='wizard-room-code-container ml-auto text-right mr-0'>
              <div className='wizard-room-code-title' data-automation='wizard-room-start-code'>
                Room Code:
              </div>
              <div className='wizard-room-code'>
                <RoomCode code={room.joinCode} />
              </div>
            </div>
          </div>
        </div>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Take Attendance'
          showDirectionInfo={true}
        />
      </div>
    ),
    readTheScript: (
      <>
        <StepContentPanel
          content={content}
          desc={mainPanelData}
          heading='Next Up: Read the Script'
          img={<div className='script-page-svg attendance-step-illustration-img' />}
          imgDesc='Student Roster'
          nextUpStyle={true}
          progressStepperMax={progressStepperMax}
          room={room}
          showProgressBar={true}
          stepNum={stepNum}
        ></StepContentPanel>
      </>
    ),
    welcome: (
      <>
        <StepContentTab
          heading='Welcome'
          showProgressBar={true}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    devicesAndMaterials: (
      <>
        <StepContentTab
          heading='Devices'
          showProgressBar={true}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          desc={mainPanelData}
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    checkDesksDiagram: (
      <>
        <StepContentTab
          heading='Check Desks'
          showProgressBar={true}
          content={content}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Check each student&rsquo;s desk for prohibited items.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    accommodationsQuestion: (
      <>
        <StepContentPanel
          content={content}
          desc={mainPanelData}
          heading='Accommodations'
          illustrationContentVerticalJustify={true}
          img={<div className='psat-attendance-step-6-svg' />}
          imgDesc='Student Roster'
          nextUpStyle={true}
          progressStepperMax={progressStepperMax}
          room={room}
          showProgressBar={true}
          stepNum={stepNum}
        ></StepContentPanel>
      </>
    ),
    aboutBreaks: (
      <>
        <StepContentTab
          heading='About Breaks'
          content={content}
          showProgressBar={true}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              to='#student-roster'
              data-automation='wizard-view-student-roster'
              className='wizard-student-roster-link cb-palette-blue5'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    startTesting: (
      <>
        <StepContentTab
          heading='Start Testing'
          showProgressBar={true}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read aloud to students.'
          rightPanel={
            <Link
              className='wizard-student-roster-link cb-palette-blue5'
              to='#student-roster'
              data-automation='wizard-view-student-roster'
            >
              {rosterLinkText}
            </Link>
          }
          desc={mainPanelData}
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
    startTheTest: (
      <>
        <StepContentTab
          heading='Start the Test'
          showProgressBar={true}
          content={content}
          darkMode={true}
          invertStyle={true}
          stepNum={stepNum}
          progressStepperMax={progressStepperMax}
          panelHeading='Read this start code aloud, write it on the board, and click "Next Step" to monitor testing.'
          rightPanel={
            <Link
              className='wizard-student-roster-link cb-palette-blue5'
              to='#student-roster'
              data-automation='wizard-view-student-roster'
            >
              {rosterLinkText}
            </Link>
          }
          desc={
            <div>
              <div className='exam-start-code'>
                <p
                  className='mb-4'
                  style={{ fontSize: '1.875rem', lineHeight: '4.375rem' }}
                  data-automation='wizard-room-start-code'
                >
                  Start Code:{' '}
                </p>
                <p className='mb-4' style={{ fontSize: '3.75rem', lineHeight: '4.375rem' }}>
                  {room?.startPin}
                </p>
              </div>
              <div className='mt-4 text-center'>
                <div>{mainPanelData}</div>
              </div>
            </div>
          }
        ></StepContentTab>
        <StudentRoster
          lastUpdated={lastUpdated}
          refetch={refetch}
          room={room}
          rosterHeading='Student Roster'
          showDirectionInfo={false}
          showInpage={true}
        />
      </>
    ),
  };

  return {
    introStep,
    outerStep,
    steps: () => {
      const rendererName = stepConfig[stepNum];
      return rendererName ? stepComponents[rendererName] : undefined;
    },
  };
}

interface AttendanceProps {
  cmsContent: CmsContent;
  flowData: FlowData;
  lastUpdated: string;
  refetch: () => void;
  room: Room;
}

function Attendance({
  cmsContent,
  flowData,
  lastUpdated = new Date().toISOString(),
  refetch = () => {},
  room,
}: AttendanceProps) {
  const { stepNum } = flowData;
  const { orgEvent } = useStateValue();
  const configKey = getConfigKey(orgEvent);
  const { outerStep: setupOuterStep } = EVENT_FLOW_CONFIGS[configKey].setup;
  const rosterLinkText = 'Update Attendance and View Student Info';

  const { steps, introStep, outerStep } = GetStepDefinitions({
    cmsContent,
    stepNum,
    room,
    lastUpdated,
    refetch,
    rosterLinkText,
    configKey,
  });

  // scroll to the roster if the URL has #student-roster
  useEffect(() => {
    if (window.location.hash === '#student-roster' && document.getElementById('student-roster')) {
      scrollTo('student-roster');
    }
  });

  useSkipLink(room.id);

  function testStarted() {
    const testStartedContent = cmsContent?.steps?.find((n) => n.step_number === outerStep);
    const testStartedText = <div dangerouslySetInnerHTML={createCleanMarkup(testStartedContent?.main_panel_content)} />;
    const detailPanelData = (
      <div dangerouslySetInnerHTML={createCleanMarkup(testStartedContent?.detail_panel_content)} />
    );
    const content = {
      main_panel_header: testStartedContent?.main_panel_header,
      main_panel_content: testStartedText,
      detail_panel_content: detailPanelData,
    };

    return (
      <StepContentPanel
        room={room}
        heading='Test Started'
        content={content}
        showStepCompletePanel={true}
        showProgressBar={false}
        desc={testStartedText}
        img={<div className='setupComplete-svg step-illustration-img mt-4' />}
        invertStyle={true}
      ></StepContentPanel>
    );
  }

  function navigation() {
    const prevButton =
      stepNum === introStep ? (
        <Link className='cb-btn cb-btn-sm cb-btn-light' to={`/rooms/get/${room?.id}/flow/setup/step/${setupOuterStep}`}>
          Back
        </Link>
      ) : stepNum > introStep ? (
        <Link
          className='cb-btn cb-btn-sm cb-btn-light'
          to={`/rooms/get/${room?.id}/flow/attendance/step/${stepNum - 1}`}
        >
          Back
        </Link>
      ) : (
        <></>
      );

    const nextButton =
      stepNum === outerStep ? (
        <Link
          className='cb-btn cb-btn-sm cb-btn-yellow ml-auto'
          data-automation='nav-nextStep-Link'
          to={`/rooms/get/${room?.id}/flow/monitor/step/1`}
        >
          Next Step
        </Link>
      ) : (
        <Link
          className='cb-btn cb-btn-sm cb-btn-yellow ml-auto'
          data-automation='nav-nextStep-Link'
          to={`/rooms/get/${room?.id}/flow/attendance/step/${stepNum + 1}`}
        >
          {stepNum >= introStep && stepNum < outerStep && 'Next Step'}
        </Link>
      );

    return <WizardNav prevButton={prevButton} nextButton={nextButton}></WizardNav>;
  }

  return (
    <>
      {stepNum === outerStep && testStarted()}
      {stepNum >= introStep && stepNum < outerStep && steps()}
      <HelpNav />
      {navigation()}
    </>
  );
}

export default Attendance;
